
// src/scripts/useScripts/useForecasts.js

import { computed, ref } from "vue";
import { db } from "@/firebase";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";

import { MAX_WEEKS_FORWARD } from "@/scripts/sharedHelpers/weeklyLimits";
import * as formatter from "@/scripts/sharedHelpers/formatter";

// =====================================================
// CONSTANTS
// =====================================================

const CATEGORIES = [
  "liquor",
  "food",
  "door",
  "other",
];

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const AUDIT_KEYS = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
];

const HISTORY_WEEKS = 12;
const AUDIT_LOOKBACK_WEEKS = 26;

// =====================================================
// DATE HELPERS
// =====================================================

function parseISO(iso) {
  const match = String(iso || "").match(
    /^(\d{4})-(\d{2})-(\d{2})$/
  );

  if (!match) {
    throw new Error(`Invalid date: ${iso}`);
  }

  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    12
  );
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  return d;
}

function addDays(date, count) {
  const d = new Date(date);
  d.setDate(d.getDate() + count);
  return d;
}

function toISODateLocal(date) {
  const d = new Date(date);

  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
}

function toISODate(date) {
  const f =
    formatter.toISODate ||
    formatter.toISO ||
    formatter.formatISODate ||
    formatter.isoDate;

  return typeof f === "function"
    ? f(date)
    : toISODateLocal(date);
}

function formatUK(date) {
  const f =
    formatter.formatUK ||
    formatter.formatUKDate ||
    formatter.ukDate ||
    formatter.formatDateUK;

  if (typeof f === "function") {
    return f(date);
  }

  const d = new Date(date);

  return [
    String(d.getDate()).padStart(2, "0"),
    String(d.getMonth() + 1).padStart(2, "0"),
    d.getFullYear(),
  ].join("/");
}

function getCurrentWeekEnding() {
  const today = startOfDay(new Date());
  const delta = (7 - today.getDay()) % 7;
  return addDays(today, delta);
}

function getWeekDates(weekISO) {
  const sunday = parseISO(weekISO);

  return DAYS.map((_, index) =>
    addDays(sunday, index - 6)
  );
}

function getWeekStartISO(weekISO) {
  return toISODateLocal(
    addDays(parseISO(weekISO), -6)
  );
}

function diffWeeks(fromISO, toISO) {
  return Math.round(
    (
      parseISO(toISO).getTime() -
      parseISO(fromISO).getTime()
    ) / 604800000
  );
}

function currentWeekStartISO() {
  return getWeekStartISO(
    toISODateLocal(getCurrentWeekEnding())
  );
}

// =====================================================
// NUMERIC HELPERS
// =====================================================

function amount(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const n = Number(
    String(value ?? "0")
      .replace(/[£,]/g, "")
      .trim()
  );

  return Number.isFinite(n) ? n : 0;
}

function average(values) {
  if (!values.length) return 0;

  return values.reduce(
    (sum, value) => sum + amount(value),
    0
  ) / values.length;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function blankRows() {
  return Object.fromEntries(
    CATEGORIES.map((category) => [
      category,
      Array(7).fill(0),
    ])
  );
}

function blankWeek(weekISO) {
  return {
    weekEndingDateISO: weekISO,
    dates: getWeekDates(weekISO),
    rows: blankRows(),
    closedDays: Array(7).fill(false),
    confirmedOpenDays: Array(7).fill(false),
    tradingPatternMessage: "",
    hasSavedCells: false,
    aiGenerated: false,
  };
}

function dayTotal(rows, dayIndex) {
  return CATEGORIES.reduce(
    (sum, category) =>
      sum + amount(rows[category]?.[dayIndex]),
    0
  );
}

// =====================================================
// FIRESTORE REFERENCES
// =====================================================

function weekDocRef(venue, weekISO) {
  return doc(
    db,
    "venues",
    venue,
    "forecast_weeks",
    weekISO
  );
}

function cellDocRef(
  venue,
  weekISO,
  category,
  dayIndex
) {
  return doc(
    db,
    "venues",
    venue,
    "forecast_weeks",
    weekISO,
    "cells",
    `${category}_${dayIndex}`
  );
}

function cellsCollection(venue, weekISO) {
  return collection(
    db,
    "venues",
    venue,
    "forecast_weeks",
    weekISO,
    "cells"
  );
}

function stateDocRef(venue) {
  return doc(
    db,
    "venues",
    venue,
    "forecast_meta",
    "state"
  );
}

function auditWeekRef(venue, mondayISO) {
  return doc(
    db,
    "venues",
    venue,
    "auditWeeks",
    mondayISO
  );
}

// =====================================================
// AUDIT CLOSED DAYS
// =====================================================

function normaliseClosedDays(data) {
  if (!data || typeof data !== "object") {
    return null;
  }

  return AUDIT_KEYS.map(
    (key) => data[key] === true
  );
}

/*
  Audit saves:
  venues/{venueId}/auditWeeks/{Monday ISO}

  {
    closedDays: {
      mon: true,
      tue: true,
      wed: false,
      ...
    }
  }

  We read actual Audit records, not a separate
  forecasting configuration.
*/

async function loadAuditHistory(venue) {
  const currentMonday = parseISO(
    currentWeekStartISO()
  );

  const dates = Array.from(
    { length: AUDIT_LOOKBACK_WEEKS },
    (_, index) =>
      toISODateLocal(
        addDays(currentMonday, -index * 7)
      )
  );

  const records = await Promise.all(
    dates.map(async (mondayISO) => {
      const snap = await getDoc(
        auditWeekRef(venue, mondayISO)
      );

      if (!snap.exists()) return null;

      const closed = normaliseClosedDays(
        snap.data()?.closedDays
      );

      if (!closed) return null;

      return {
        mondayISO,
        closed,
      };
    })
  );

  return records
    .filter(Boolean)
    .sort(
      (a, b) =>
        b.mondayISO.localeCompare(a.mondayISO)
    );
}

async function loadExactAuditWeeks(
  venue,
  weekISOs,
  history
) {
  const map = Object.fromEntries(
    history.map((record) => [
      record.mondayISO,
      record,
    ])
  );

  await Promise.all(
    weekISOs.map(async (weekISO) => {
      const mondayISO = getWeekStartISO(weekISO);

      if (map[mondayISO]) return;

      const snap = await getDoc(
        auditWeekRef(venue, mondayISO)
      );

      if (!snap.exists()) return;

      const closed = normaliseClosedDays(
        snap.data()?.closedDays
      );

      if (!closed) return;

      map[mondayISO] = {
        mondayISO,
        closed,
      };
    })
  );

  return map;
}

/*
  For future weeks:

  - The two most recent earlier recorded Audit
    weeks establish the pattern.
  - Both must say OPEN for a weekday to be
    forecast as open.
  - One exceptional opening is insufficient.
  - A week-specific Audit record overrides
    the inferred pattern for that week.
  - Missing records do not count as OPEN.
*/


function resolveTradingPattern(
  weekISO,
  auditHistory,
  exactAuditMap
) {
  const mondayISO = getWeekStartISO(weekISO);
  const exact = exactAuditMap[mondayISO];

  // If this specific week has an Audit record,
  // use its saved closed-day settings.
  if (exact) {
    return {
      closedDays: [...exact.closed],
      confirmedOpenDays:
        exact.closed.map((closed) => !closed),
      message:
        "Trading days taken from this week's " +
        "saved Venue Audit.",
    };
  }

  // NEW: If the venue has no saved Audit history,
  // assume all seven days are open.
  if (auditHistory.length === 0) {
    return {
      closedDays: Array(7).fill(false),
      confirmedOpenDays: Array(7).fill(true),
      message:
        "No saved Audit weeks; all days assumed open.",
    };
  }

  // Existing two-week confirmation logic.
  const previous = auditHistory
    .filter(
      (record) =>
        record.mondayISO < mondayISO
    )
    .slice(0, 2);

  if (previous.length < 2) {
    return {
      closedDays: Array(7).fill(true),
      confirmedOpenDays: Array(7).fill(false),
      message:
        "Fewer than two previous Audit weeks " +
        "are available. Future opening days " +
        "cannot yet be confirmed.",
    };
  }

  const confirmedOpenDays = AUDIT_KEYS.map(
    (_, index) =>
      previous[0].closed[index] === false &&
      previous[1].closed[index] === false
  );

  return {
    closedDays:
      confirmedOpenDays.map((open) => !open),
    confirmedOpenDays,
    message:
      "Future trading pattern inferred from " +
      "the last two recorded Venue Audit weeks. " +
      "A weekday is forecast as open only " +
      "when both records confirm it.",
  };
}


// =====================================================
// ACTUAL TRADING DATA
// =====================================================

function extractTillTotal(data = {}) {
  const direct =
    data.counted_total ??
    data.countedTotal ??
    data.total_counted ??
    data.actual_total ??
    data.actualTotal ??
    data.total ??
    data.grand_total ??
    data.grandTotal ??
    data.value ??
    data.amount ??
    data?.totals?.counted_total ??
    data?.totals?.total;

  if (direct != null && direct !== "") {
    return amount(direct);
  }

  const ignored = new Set([
    "name",
    "label",
    "type",
    "id",
    "uid",
    "email",
    "created_at",
    "updated_at",
    "createdAt",
    "updatedAt",
    "venue_id",
    "venueId",
    "trading_date",
    "tradingDate",
    "week_ending_date",
  ]);

  return Object.entries(data).reduce(
    (sum, [key, value]) => {
      if (ignored.has(key)) return sum;

      if (
        typeof value === "number" &&
        Number.isFinite(value)
      ) {
        return sum + value;
      }

      return sum;
    },
    0
  );
}

async function fetchActualDay(venue, dateISO) {
  const snap = await getDoc(
    doc(
      db,
      "venues",
      venue,
      "tills_days",
      dateISO
    )
  );

  if (snap.exists()) {
    const data = snap.data() || {};

    const rolled =
      data.counted_total ??
      data.countedTotal ??
      data.total_counted ??
      data.actual_total ??
      data.actualTotal ??
      data.total ??
      data?.totals?.counted_total;

    if (rolled != null && rolled !== "") {
      return {
        exists: true,
        value: amount(rolled),
      };
    }
  }

  const tills = await getDocs(
    collection(
      db,
      "venues",
      venue,
      "tills_days",
      dateISO,
      "tills"
    )
  );

  if (tills.empty) {
    return {
      exists: false,
      value: 0,
    };
  }

  let total = 0;

  tills.forEach((item) => {
    total += extractTillTotal(
      item.data() || {}
    );
  });

  return {
    exists: true,
    value: total,
  };
}

async function mapWithLimit(
  items,
  concurrency,
  callback
) {
  const results = new Array(items.length);
  let next = 0;

  async function worker() {
    while (next < items.length) {
      const index = next++;
      results[index] = await callback(
        items[index],
        index
      );
    }
  }

  await Promise.all(
    Array.from(
      {
        length: Math.min(
          concurrency,
          items.length
        ),
      },
      () => worker()
    )
  );

  return results;
}

async function loadTradingHistory(venue) {
  const sunday = getCurrentWeekEnding();
  const todayISO = toISODateLocal(new Date());
  const requests = [];

  for (
    let back = 0;
    back <= HISTORY_WEEKS;
    back++
  ) {
    for (let day = 0; day < 7; day++) {
      const date = addDays(
        sunday,
        day - 6 - back * 7
      );

      const iso = toISODateLocal(date);

      if (iso > todayISO) continue;

      requests.push({
        back,
        day,
        iso,
      });
    }
  }

  return mapWithLimit(
    requests,
    8,
    async (item) => ({
      ...item,
      actual: await fetchActualDay(
        venue,
        item.iso
      ),
    })
  );
}

// =====================================================
// RUN RATE / TREND
// =====================================================

function calculateRunRate(history, dayIndex) {
  const records = history.filter(
    (item) => item.day === dayIndex
  );

  const current = records.find(
    (item) => item.back === 0
  );

  // Match existing Comparisons Run Rate:
  // include the current week when a current-day
  // actual exists; otherwise use prior weeks.

  const selected = current?.actual?.exists
    ? records.filter(
        (item) => item.back <= 5
      )
    : records.filter(
        (item) =>
          item.back >= 1 &&
          item.back <= 6
      );

  let values = selected
    .filter((item) => item.actual.exists)
    .map((item) => item.actual.value);

  // If the six-week window is empty, use older
  // matching weekdays that are available.
  if (!values.length) {
    values = records
      .filter((item) => item.actual.exists)
      .slice(0, 12)
      .map((item) => item.actual.value);
  }

  return {
    value: average(values),
    samples: values.length,
  };
}

function calculateTrend(history) {
  const completeWeeks = [];

  for (let back = 1; back <= 12; back++) {
    const records = history.filter(
      (item) =>
        item.back === back &&
        item.actual.exists
    );

    if (records.length !== 7) continue;

    completeWeeks.push({
      back,
      total: records.reduce(
        (sum, item) =>
          sum + item.actual.value,
        0
      ),
    });
  }

  const recent = completeWeeks
    .filter((item) => item.back <= 6)
    .map((item) => item.total);

  const older = completeWeeks
    .filter((item) => item.back >= 7)
    .map((item) => item.total);

  if (
    recent.length < 3 ||
    older.length < 3
  ) {
    return {
      available: false,
      percent: 0,
    };
  }

  const baseline = average(older);

  if (baseline <= 0) {
    return {
      available: false,
      percent: 0,
    };
  }

  return {
    available: true,
    percent: clamp(
      (
        average(recent) / baseline - 1
      ) * 100,
      -30,
      30
    ),
  };
}

// =====================================================
// CATEGORY MIX
// =====================================================

function normaliseMix(totals) {
  const sum = CATEGORIES.reduce(
    (total, category) =>
      total + Math.max(
        0,
        amount(totals[category])
      ),
    0
  );

  if (sum <= 0) return null;

  return Object.fromEntries(
    CATEGORIES.map((category) => [
      category,
      Math.max(
        0,
        amount(totals[category])
      ) / sum,
    ])
  );
}

function mixFromRows(rows) {
  const totals = {};

  for (const category of CATEGORIES) {
    totals[category] = (
      rows[category] || []
    ).reduce(
      (sum, value) =>
        sum + Math.max(0, amount(value)),
      0
    );
  }

  return normaliseMix(totals);
}

async function loadHistoricalMix(venue) {
  const totals = Object.fromEntries(
    CATEGORIES.map((category) => [
      category,
      0,
    ])
  );

  const sunday = getCurrentWeekEnding();

  const weekISOs = Array.from(
    { length: 6 },
    (_, index) =>
      toISODateLocal(
        addDays(
          sunday,
          -(index + 1) * 7
        )
      )
  );

  const snapshots = await Promise.all(
    weekISOs.map((weekISO) =>
      getDocs(
        cellsCollection(venue, weekISO)
      )
    )
  );

  for (const snap of snapshots) {
    snap.forEach((item) => {
      const data = item.data() || {};
      const category = String(
        data.category || ""
      );

      if (!CATEGORIES.includes(category)) {
        return;
      }

      totals[category] += Math.max(
        0,
        amount(data.value)
      );
    });
  }

  return normaliseMix(totals);
}

function allocateDaily(total, mix) {
  const result = {};
  let remaining = total;

  CATEGORIES.forEach((category, index) => {
    if (index === CATEGORIES.length - 1) {
      result[category] = remaining;
      return;
    }

    const value = Math.min(
      remaining,
      Math.round(
        total * mix[category]
      )
    );

    result[category] = value;
    remaining -= value;
  });

  return result;
}

// =====================================================
// FORECAST COMPOSABLE
// =====================================================

export function useForecasts({
  venueIdRef,
  isAdminRef,
  userRef,
}) {
  const loading = ref(false);
  const error = ref("");
  const aiLoading = ref(false);
  const aiError = ref("");

  const weeks = ref([]);
  const statusByWeek = ref({});
  const adjustMode = ref({});

  const lockState = ref({
    current_week_ending: null,
    locked_through_week_ending: null,
  });

  const currentWE = computed(
    () => getCurrentWeekEnding()
  );

  const baseWeeks = computed(() => {
    const max = Number(
      MAX_WEEKS_FORWARD || 0
    );

    return Array.from(
      { length: max + 1 },
      (_, index) =>
        toISODateLocal(
          addDays(
            currentWE.value,
            index * 7
          )
        )
    );
  });

  // -------------------------------------------------
  // PERMISSIONS
  // -------------------------------------------------

  function isInCurrentPlus2Window(weekISO) {
    const state = lockState.value;

    if (
      state.current_week_ending &&
      state.locked_through_week_ending
    ) {
      return (
        weekISO >= state.current_week_ending &&
        weekISO <=
          state.locked_through_week_ending
      );
    }

    const difference = diffWeeks(
      toISODateLocal(currentWE.value),
      weekISO
    );

    return (
      difference >= 0 &&
      difference <= 2
    );
  }

  function isSubmitted(weekISO) {
    return Boolean(
      statusByWeek.value[weekISO]
        ?.submitted_at
    );
  }

  function canSubmit(weekISO) {
    if (!venueIdRef.value) return false;

    if (
      !isAdminRef.value &&
      isInCurrentPlus2Window(weekISO)
    ) return false;

    return !isSubmitted(weekISO);
  }

  function canAdjust(weekISO) {
    if (!venueIdRef.value) return false;

    if (
      !isAdminRef.value &&
      isInCurrentPlus2Window(weekISO)
    ) return false;

    return isSubmitted(weekISO);
  }

  function isEditable(weekISO) {
    if (!venueIdRef.value) return false;

    if (
      !isAdminRef.value &&
      isInCurrentPlus2Window(weekISO)
    ) return false;

    if (!isSubmitted(weekISO)) {
      return true;
    }

    return Boolean(
      adjustMode.value[weekISO]
    );
  }

  function setAdjustMode(weekISO, enabled) {
    adjustMode.value = {
      ...adjustMode.value,
      [weekISO]: enabled,
    };
  }

  // -------------------------------------------------
  // LOAD FORECASTS / AUDIT PATTERN
  // -------------------------------------------------

  let refreshSequence = 0;
  let auditHistoryCache = [];
  let predictionCache = null;
  let predictionCacheVenue = "";
  let predictionCacheTime = 0;

  // Keep manual writes ordered per week.
  const pendingWrites = new Map();

  function queueWrite(weekISO, operation) {
    const previous =
      pendingWrites.get(weekISO) ||
      Promise.resolve();

    const next = previous
      .catch(() => {})
      .then(operation);

    pendingWrites.set(weekISO, next);

    next.finally(() => {
      if (
        pendingWrites.get(weekISO) === next
      ) {
        pendingWrites.delete(weekISO);
      }
    }).catch(() => {});

    return next;
  }

  async function waitForWrites(weekISO) {
    const pending =
      pendingWrites.get(weekISO);

    if (pending) await pending;
  }

  async function loadLockState(venue) {
    const snap = await getDoc(
      stateDocRef(venue)
    );

    const data = snap.exists()
      ? snap.data()
      : {};

    return {
      current_week_ending:
        data.current_week_ending || null,
      locked_through_week_ending:
        data.locked_through_week_ending || null,
    };
  }

  async function loadWeek(venue, weekISO) {
    const model = blankWeek(weekISO);

    const [parent, cells] = await Promise.all([
      getDoc(weekDocRef(venue, weekISO)),
      getDocs(cellsCollection(venue, weekISO)),
    ]);

    const parentData = parent.exists()
      ? parent.data()
      : {};

    const status = {
      submitted_at:
        parentData.submitted_at || null,
      submitted_by_uid:
        parentData.submitted_by_uid || null,
      submitted_by_email:
        parentData.submitted_by_email || null,
      last_adjusted_at:
        parentData.last_adjusted_at || null,
    };

    model.hasSavedCells = !cells.empty;
    model.aiGenerated = Boolean(
      parentData.ai_generated_at
    );

    cells.forEach((item) => {
      const data = item.data() || {};
      const category = String(
        data.category || ""
      );
      const day = Number(data.day_index);

      if (
        !CATEGORIES.includes(category) ||
        !Number.isInteger(day) ||
        day < 0 ||
        day > 6
      ) return;

      model.rows[category][day] =
        amount(data.value);
    });

    return { model, status };
  }

  async function refreshAll() {
    const venue = venueIdRef.value;
    const sequence = ++refreshSequence;

    if (!venue) {
      weeks.value = [];
      statusByWeek.value = {};
      return;
    }

    loading.value = true;
    error.value = "";

    try {
      const weekISOs = [...baseWeeks.value];

      const [
        newLockState,
        auditHistory,
        loadedWeeks,
      ] = await Promise.all([
        loadLockState(venue),
        loadAuditHistory(venue),
        mapWithLimit(
          weekISOs,
          5,
          (weekISO) =>
            loadWeek(venue, weekISO)
        ),
      ]);

      if (
        sequence !== refreshSequence ||
        venue !== venueIdRef.value
      ) return;

      const exactAuditMap =
        await loadExactAuditWeeks(
          venue,
          weekISOs,
          auditHistory
        );

      if (
        sequence !== refreshSequence ||
        venue !== venueIdRef.value
      ) return;

      const models = [];
      const statuses = {};

      for (const item of loadedWeeks) {
        const model = item.model;
        const weekISO =
          model.weekEndingDateISO;

        statuses[weekISO] = item.status;

        const pattern =
          resolveTradingPattern(
            weekISO,
            auditHistory,
            exactAuditMap
          );

        if (!item.status.submitted_at) {
          model.closedDays =
            pattern.closedDays;
          model.confirmedOpenDays =
            pattern.confirmedOpenDays;
          model.tradingPatternMessage =
            pattern.message;
        } else {
          // Never apply inferred closures to
          // an already submitted forecast.
          model.closedDays =
            Array(7).fill(false);
          model.confirmedOpenDays =
            Array(7).fill(true);
          model.tradingPatternMessage =
            "Submitted forecast: saved figures " +
            "are preserved.";
        }

        models.push(model);
      }

      lockState.value = newLockState;
      auditHistoryCache = auditHistory;
      statusByWeek.value = statuses;
      weeks.value = models;
      adjustMode.value = {};
    } catch (e) {
      if (sequence === refreshSequence) {
        error.value =
          e?.message || String(e);
      }
    } finally {
      if (sequence === refreshSequence) {
        loading.value = false;
      }
    }
  }

  // -------------------------------------------------
  // PREDICTION DATA
  // -------------------------------------------------

  async function getPredictionData(
    venue,
    force = false
  ) {
    if (
      !force &&
      predictionCacheVenue === venue &&
      predictionCache &&
      Date.now() - predictionCacheTime <
        120000
    ) {
      return predictionCache;
    }

    const [history, mix] =
      await Promise.all([
        loadTradingHistory(venue),
        loadHistoricalMix(venue),
      ]);

    const result = {
      rates: DAYS.map((_, index) =>
        calculateRunRate(history, index)
      ),
      trend: calculateTrend(history),
      mix,
    };

    predictionCache = result;
    predictionCacheVenue = venue;
    predictionCacheTime = Date.now();

    return result;
  }

  function buildPrediction(week, data) {
    const rows = blankRows();

    const mix =
      data.mix ||
      mixFromRows(week.rows) ||
      {
        liquor: 1,
        food: 0,
        door: 0,
        other: 0,
      };

    const adjustment =
      data.trend.available
        ? data.trend.percent * 0.35 / 100
        : 0;

    let estimatedDays = 0;

    for (let day = 0; day < 7; day++) {
      if (week.closedDays[day]) {
        continue;
      }

      const rate = data.rates[day];

      // Each weekday is independent.
      // One recorded trading day is enough.
      if (!rate || rate.samples === 0) {
        continue;
      }

      const total = Math.max(
        0,
        Math.round(
          rate.value * (1 + adjustment)
        )
      );

      const allocation = allocateDaily(
        total,
        mix
      );

      for (const category of CATEGORIES) {
        rows[category][day] =
          allocation[category];
      }

      estimatedDays++;
    }

    if (estimatedDays === 0) {
      throw new Error(
        "No confirmed open weekdays with " +
        "historical trading data are available " +
        "for this forecast."
      );
    }

    return rows;
  }

  // -------------------------------------------------
  // TRANSACTIONAL WRITES
  // -------------------------------------------------

  async function assertWeekPermission(
    venue,
    weekISO,
    allowSubmitted = false
  ) {
    if (venue !== venueIdRef.value) {
      throw new Error(
        "Selected venue has changed."
      );
    }

    const [lock, parent] =
      await Promise.all([
        loadLockState(venue),
        getDoc(weekDocRef(venue, weekISO)),
      ]);

    lockState.value = lock;

    if (
      !isAdminRef.value &&
      isInCurrentPlus2Window(weekISO)
    ) {
      throw new Error(
        "This forecast week is locked."
      );
    }

    const submitted = Boolean(
      parent.exists() &&
      parent.data()?.submitted_at
    );

    if (
      submitted &&
      (
        !allowSubmitted ||
        !adjustMode.value[weekISO]
      )
    ) {
      throw new Error(
        "This submitted forecast is locked."
      );
    }

    return submitted;
  }

  function userFields() {
    return {
      updated_by_uid:
        userRef.value?.uid || null,
      updated_by_email:
        userRef.value?.email || null,
    };
  }

  async function saveRows(
    venue,
    week,
    rows,
    {
      automatic = false,
      markAI = false,
    } = {}
  ) {
    const weekISO =
      week.weekEndingDateISO;

    await assertWeekPermission(
      venue,
      weekISO,
      !automatic
    );

    const parentRef =
      weekDocRef(venue, weekISO);

    await runTransaction(
      db,
      async (tx) => {
        const parent =
          await tx.get(parentRef);

        const submitted = Boolean(
          parent.exists() &&
          parent.data()?.submitted_at
        );

        if (
          submitted &&
          (
            automatic ||
            !adjustMode.value[weekISO]
          )
        ) {
          throw new Error(
            "Submitted forecast is locked."
          );
        }

        const cells = [];

        for (let day = 0; day < 7; day++) {
          for (const category of CATEGORIES) {
            cells.push({
              day,
              category,
              ref: cellDocRef(
                venue,
                weekISO,
                category,
                day
              ),
            });
          }
        }

        // All transaction reads precede writes.
        const existing = await Promise.all(
          cells.map((cell) =>
            tx.get(cell.ref)
          )
        );

        if (
          automatic &&
          existing.some(
            (snap) => snap.exists()
          )
        ) {
          throw new Error(
            "Existing forecast cells found."
          );
        }

        const stamp = serverTimestamp();

        tx.set(
          parentRef,
          {
            week_ending_date: weekISO,
            updated_at: stamp,
            ...userFields(),
            ...(markAI
              ? {
                  ai_generated_at: stamp,
                  ai_method:
                    "audit_two_week_confirmed_run_rate",
                }
              : {}),
          },
          { merge: true }
        );

        for (const cell of cells) {
          const value =
            !submitted &&
            week.closedDays[cell.day]
              ? 0
              : Math.max(
                  0,
                  amount(
                    rows[cell.category][cell.day]
                  )
                );

          tx.set(
            cell.ref,
            {
              week_ending_date: weekISO,
              category: cell.category,
              day_index: cell.day,
              trading_date: toISODateLocal(
                week.dates[cell.day]
              ),
              value,
              updated_at: stamp,
              ...userFields(),
            },
            { merge: true }
          );
        }
      }
    );
  }

  // -------------------------------------------------
  // AUTO-POPULATE EMPTY WEEKS
  // -------------------------------------------------

  async function autoPopulateForecasts() {
    const venue = venueIdRef.value;

    if (
      !venue ||
      loading.value ||
      aiLoading.value
    ) return;

    const candidates = weeks.value.filter(
      (week) =>
        !week.hasSavedCells &&
        !isSubmitted(
          week.weekEndingDateISO
        ) &&
        isEditable(
          week.weekEndingDateISO
        ) &&
        week.confirmedOpenDays.some(Boolean)
    );

    if (!candidates.length) return;

    aiLoading.value = true;
    aiError.value = "";

    try {
      const data =
        await getPredictionData(venue);

      if (venue !== venueIdRef.value) {
        return;
      }

      let successful = 0;

      for (const week of candidates) {
        const weekISO =
          week.weekEndingDateISO;

        if (
          venue !== venueIdRef.value ||
          !isEditable(weekISO)
        ) break;

        try {
          const rows =
            buildPrediction(week, data);

          await queueWrite(
            weekISO,
            () =>
              saveRows(
                venue,
                week,
                rows,
                {
                  automatic: true,
                  markAI: true,
                }
              )
          );

          for (const category of CATEGORIES) {
            week.rows[category] = [
              ...rows[category],
            ];
          }

          week.hasSavedCells = true;
          week.aiGenerated = true;
          successful++;
        } catch (e) {
          console.warn(
            "[Forecast] Auto-populate skipped:",
            weekISO,
            e
          );
        }
      }

      if (!successful) {
        aiError.value =
          "No eligible forecast could be " +
          "generated. Check the available " +
          "Audit trading pattern and actual " +
          "trading history.";
      }
    } catch (e) {
      aiError.value =
        e?.message || String(e);
    } finally {
      aiLoading.value = false;
    }
  }

  // -------------------------------------------------
  // REGENERATE ON REQUEST
  // -------------------------------------------------

  async function regenerateWeek(weekISO) {
    const venue = venueIdRef.value;

    const week = weeks.value.find(
      (item) =>
        item.weekEndingDateISO === weekISO
    );

    if (
      !week ||
      !isEditable(weekISO)
    ) {
      throw new Error(
        "This forecast cannot be adjusted."
      );
    }

    aiLoading.value = true;
    aiError.value = "";

    try {
      await waitForWrites(weekISO);

      await assertWeekPermission(
        venue,
        weekISO,
        true
      );

      const data =
        await getPredictionData(
          venue,
          true
        );

      const rows =
        buildPrediction(week, data);

      if (venue !== venueIdRef.value) {
        throw new Error(
          "Selected venue has changed."
        );
      }

      await queueWrite(
        weekISO,
        () =>
          saveRows(
            venue,
            week,
            rows,
            {
              automatic: false,
              markAI: true,
            }
          )
      );

      for (const category of CATEGORIES) {
        week.rows[category] = [
          ...rows[category],
        ];
      }

      week.hasSavedCells = true;
      week.aiGenerated = true;
    } catch (e) {
      aiError.value =
        e?.message || String(e);
      throw e;
    } finally {
      aiLoading.value = false;
    }
  }

  // -------------------------------------------------
  // MANUAL CELL EDITING
  // -------------------------------------------------

  async function persistCell(
    venue,
    weekISO,
    category,
    dayIndex,
    value
  ) {
    await assertWeekPermission(
      venue,
      weekISO,
      true
    );

    const parentRef =
      weekDocRef(venue, weekISO);

    await runTransaction(
      db,
      async (tx) => {
        const parent =
          await tx.get(parentRef);

        const submitted = Boolean(
          parent.exists() &&
          parent.data()?.submitted_at
        );

        if (
          submitted &&
          !adjustMode.value[weekISO]
        ) {
          throw new Error(
            "Submitted forecast is locked."
          );
        }

        const stamp = serverTimestamp();

        tx.set(
          parentRef,
          {
            week_ending_date: weekISO,
            updated_at: stamp,
            ...userFields(),
          },
          { merge: true }
        );

        tx.set(
          cellDocRef(
            venue,
            weekISO,
            category,
            dayIndex
          ),
          {
            week_ending_date: weekISO,
            category,
            day_index: dayIndex,
            trading_date: toISODateLocal(
              getWeekDates(weekISO)[dayIndex]
            ),
            value,
            updated_at: stamp,
            ...userFields(),
          },
          { merge: true }
        );
      }
    );
  }

  async function setCell(
    weekISO,
    category,
    dayIndex,
    value
  ) {
    const venue = venueIdRef.value;

    if (
      !isEditable(weekISO) ||
      !CATEGORIES.includes(category) ||
      !Number.isInteger(dayIndex) ||
      dayIndex < 0 ||
      dayIndex > 6
    ) return;

    const week = weeks.value.find(
      (item) =>
        item.weekEndingDateISO === weekISO
    );

    if (!week) return;

    if (
      !isSubmitted(weekISO) &&
      week.closedDays[dayIndex]
    ) return;

    const next = Math.max(
      0,
      amount(value)
    );

    const previous =
      week.rows[category][dayIndex];

    week.rows[category][dayIndex] =
      next;

    try {
      await queueWrite(
        weekISO,
        () =>
          persistCell(
            venue,
            weekISO,
            category,
            dayIndex,
            next
          )
      );

      week.hasSavedCells = true;
    } catch (e) {
      if (venue === venueIdRef.value) {
        week.rows[category][dayIndex] =
          previous;
        error.value =
          e?.message || String(e);
      }
    }
  }

  // -------------------------------------------------
  // SUBMIT WEEK
  // -------------------------------------------------

  async function submitWeek(weekISO) {
    const venue = venueIdRef.value;

    if (!canSubmit(weekISO)) return;

    loading.value = true;
    error.value = "";

    try {
      await waitForWrites(weekISO);

      await assertWeekPermission(
        venue,
        weekISO,
        false
      );

      const week = weeks.value.find(
        (item) =>
          item.weekEndingDateISO === weekISO
      );

      if (!week) {
        throw new Error(
          "Forecast week not found."
        );
      }

      const parentRef =
        weekDocRef(venue, weekISO);

      await runTransaction(
        db,
        async (tx) => {
          const parent =
            await tx.get(parentRef);

          if (
            parent.exists() &&
            parent.data()?.submitted_at
          ) {
            throw new Error(
              "Forecast already submitted."
            );
          }

          const stamp =
            serverTimestamp();

          tx.set(
            parentRef,
            {
              week_ending_date: weekISO,
              submitted_at: stamp,
              submitted_by_uid:
                userRef.value?.uid || null,
              submitted_by_email:
                userRef.value?.email || null,
              updated_at: stamp,
              ...userFields(),
            },
            { merge: true }
          );

          for (
            let day = 0;
            day < 7;
            day++
          ) {
            for (const category of CATEGORIES) {
              const value =
                week.closedDays[day]
                  ? 0
                  : Math.max(
                      0,
                      amount(
                        week.rows[category][day]
                      )
                    );

              tx.set(
                cellDocRef(
                  venue,
                  weekISO,
                  category,
                  day
                ),
                {
                  week_ending_date: weekISO,
                  category,
                  day_index: day,
                  trading_date: toISODateLocal(
                    week.dates[day]
                  ),
                  value,
                  updated_at: stamp,
                  ...userFields(),
                },
                { merge: true }
              );
            }
          }
        }
      );

      setAdjustMode(weekISO, false);

      if (venue === venueIdRef.value) {
        await refreshAll();
      }
    } catch (e) {
      error.value =
        e?.message || String(e);
    } finally {
      loading.value = false;
    }
  }

  // -------------------------------------------------
  // ADJUSTMENT MODE
  // -------------------------------------------------

  async function enableAdjust(weekISO) {
    if (!canAdjust(weekISO)) return;

    const venue = venueIdRef.value;

    try {
      const snap = await getDoc(
        weekDocRef(venue, weekISO)
      );

      if (
        !snap.exists() ||
        !snap.data()?.submitted_at
      ) {
        throw new Error(
          "Forecast is not submitted."
        );
      }

      if (
        !isAdminRef.value &&
        isInCurrentPlus2Window(weekISO)
      ) {
        throw new Error(
          "Forecast week is locked."
        );
      }

      setAdjustMode(weekISO, true);
    } catch (e) {
      setAdjustMode(weekISO, false);
      error.value =
        e?.message || String(e);
    }
  }

  async function disableAdjust(weekISO) {
    try {
      await waitForWrites(weekISO);
    } catch (e) {
      error.value =
        e?.message || String(e);
    }

    setAdjustMode(weekISO, false);
  }

  // -------------------------------------------------
  // PUBLIC API
  // -------------------------------------------------

  return {
    CATEGORIES,
    DAYS,
    formatUK,

    loading,
    error,
    aiLoading,
    aiError,

    weeks,
    statusByWeek,

    currentWE: computed(
      () => toISODateLocal(currentWE.value)
    ),

    baseWeeks,

    isInCurrentPlus2Window,
    isSubmitted,
    canSubmit,
    canAdjust,
    isEditable,

    refreshAll,
    autoPopulateForecasts,
    regenerateWeek,

    setCell,
    submitWeek,
    enableAdjust,
    disableAdjust,
  };
}

// =====================================================
// COMPARISONS COMPATIBILITY
// DO NOT REMOVE THIS EXPORT
// =====================================================

export async function fetchForecastWeekDailyTotals({
  venue_id,
  week_ending_date,
}) {
  if (!venue_id) {
    throw new Error("Missing venue_id");
  }

  if (!week_ending_date) {
    throw new Error(
      "Missing week_ending_date"
    );
  }

  const snap = await getDocs(
    cellsCollection(
      venue_id,
      week_ending_date
    )
  );

  const totals = Array(7).fill(0);

  snap.forEach((item) => {
    const data = item.data() || {};
    const day = Number(
      data.day_index
    );

    if (
      !Number.isInteger(day) ||
      day < 0 ||
      day > 6
    ) return;

    totals[day] += amount(
      data.value
    );
  });

  return totals;
}
